import { MessageBarType, Stack, Spinner, SpinnerSize } from '@fluentui/react';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MessageContext, TenantContext, UserContext } from './contexts/ContextWrapper';
import { useSettingsFetch } from './hooks/useSettingsFetch';
import { MessageContextType } from './interfaces/ContextType';
import { GetMyMenuItems } from './helpers/MenuHelper';
import { AuthContext } from './contexts/AuthContext';
import { DomainItem } from './interfaces/DomainItem';
import { useSession } from './hooks/useSession';
import { CenterComponent } from './components/common/CenterComponent';

// Lazy load all page components for better bundle splitting
const HomeTenant = React.lazy(() => import('./components/home/HomeTenant'));
const Settings = React.lazy(() => import('./components/settings/Settings'));
const Templates = React.lazy(() => import('./components/templates/Templates'));
const Icons = React.lazy(() => import('./components/icons/Icons'));
const Pictures = React.lazy(() => import('./components/pictures/Pictures'));
const PictureBank = React.lazy(() => import('./components/picturebank/PictureBank'));
const Slides = React.lazy(() => import('./components/slides/Slides'));
const WordContent = React.lazy(() => import('./components/wordcontent/WordContent'));
const Sections = React.lazy(() => import('./components/sections/Sections'));
const EmailSignatures = React.lazy(() => import('./components/emailsignature/EmailSignatures'));
const CustomData = React.lazy(() => import('./components/customdata/CustomData'));
const UserData = React.lazy(() => import('./components/userdata/UserData'));
const Accounts = React.lazy(() => import('./components/accounts/Accounts'));
const Groups = React.lazy(() => import('./components/groups/Groups'));
const UserDataGroups = React.lazy(() => import('./components/userdatagroups/UserDataGroups'));
const Statistics = React.lazy(() => import('./components/statistics/Statistics'));
const Domains = React.lazy(() => import('./components/domains/Domains'));
const AdminAccounts = React.lazy(() => import('./components/adminaccounts/AdminAccounts'));
const Customers = React.lazy(() => import('./components/customers/Customers'));
const Academy = React.lazy(() => import('./components/academy/Academy'));
const Tutorials = React.lazy(() => import('./components/tutorials/Tutorials'));
const Excel2Web = React.lazy(() => import('./components/excel2web/Excel2Web'));
const OutlookContent = React.lazy(() => import('./components/outlookcontent/OutlookContent'));
const Esef = React.lazy(() => import('./components/esef/Esef'));
const Excel2Users = React.lazy(() => import('./components/excel2users/Excel2Users'));
const AI = React.lazy(() => import('./components/ai/AI'));

// Fallback spinner for lazy loaded components
const LazyFallback = () => (
	<CenterComponent>
		<Spinner size={SpinnerSize.large} label='Loading...' />
	</CenterComponent>
);

interface MainPageProps {
	setSelectedPage: (x: string) => void;
}

const MainPage: React.FC<MainPageProps> = (props) => {

	const { page } = useParams<'page'>();

	const { tenantContext } = React.useContext(TenantContext);
	const { messageContext, setMessageContext } = React.useContext(MessageContext);
	const { userContext, setUserContext } = React.useContext(UserContext);

	const { accessToken } = React.useContext(AuthContext);
	const { UpdateSettings } = useSettingsFetch()
	const { LoginSession } = useSession()
	const navigate = useNavigate();

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	let main: JSX.Element = (<></>)

	React.useEffect(() => {
		setIsLoading(true);
		if (!tenantContext.settings && userContext.userData && tenantContext.api && tenantContext.tenantName) {
			UpdateSettings(tenantContext.api, tenantContext.tenantName, userContext.userData)
				.then(() => {
					setIsLoading(false);
				})
				.catch(() => {
					setIsLoading(false);
				});
		} else if (tenantContext.settings) {
			setIsLoading(false);
		}
	}, [tenantContext.settings, userContext.userData, tenantContext.api, tenantContext.tenantName, UpdateSettings])

	const myPages: string[] = React.useMemo(() => {
		return GetMyMenuItems(userContext.userData, tenantContext.settings, userContext.adminMode)
	}, [userContext.userData, tenantContext.settings, userContext.adminMode])

	const domainVerified: boolean = React.useMemo(() => {
		if (!userContext.userData || !tenantContext.api || !tenantContext.tenantName) { return true }
		const domain: DomainItem | undefined = userContext.userData?.Domains?.find(
			(d) => (d.APIName.toLowerCase() === tenantContext.api.toLowerCase() && d.DomainName.toLowerCase() === tenantContext.tenantName.toLowerCase())
		)
		return domain?.TenantExists || tenantContext.settings.InternalUse
	}, [tenantContext.api, tenantContext.tenantName, userContext.userData])

	React.useEffect(() => {
		if (page && myPages.includes(page.toLowerCase())) {
			props.setSelectedPage(page!)
			localStorage.setItem('selectedPage', page!)
		} else if (page && page.toLowerCase() !== 'home' && !myPages.includes(page.toLowerCase())) {
			// Redirect to home if user doesn't have permission for the page
			navigate(`/${tenantContext.api.toLowerCase()}/${tenantContext.tenantName.toLowerCase()}/home`)
		}

		if (!domainVerified) {
			setMessageContext({ ...messageContext, message: 'Tenant not verified', messageType: MessageBarType.severeWarning, visible: true })
		}
		else { setMessageContext({ ...messageContext, visible: false }) }
	}, [page, myPages, navigate, tenantContext.api, tenantContext.tenantName])

	const UpdateMessage = React.useCallback((message: MessageContextType) => {
		setMessageContext({ ...messageContext, message: message.message, messageType: message.messageType, visible: message.visible });
	}, [messageContext, setMessageContext]);

	if (isLoading) {
		return (
			<CenterComponent>
				<Spinner size={SpinnerSize.large} label="Loading page data..." />
			</CenterComponent>
		);
	}

	// Token is automatically refreshed by MSAL - no manual check needed

	switch (page) {
		case '':
		case 'home':
			main = (
				<HomeTenant />
			)
			break;
		case 'settings':
			main = (
				<Settings />
			)
			break;
		case 'templates':
			main = (
				<Templates />
			)
			break;
		case 'icons':
			main = (
				<Icons />
			)
			break;
		case 'pictures':
			main = (
				<Pictures
					setMessage={(message) => { UpdateMessage(message) }}
					accessToken={accessToken!}
					domain={tenantContext.tenantName}
					api={tenantContext.api}
					settings={tenantContext.settings}
					userData={userContext.userData}
				/>
			)
			break;
		case 'picturebank':
			main = (
				<PictureBank
					setMessage={(message) => { UpdateMessage(message) }}
					accessToken={accessToken!}
					domain={tenantContext.tenantName}
					api={tenantContext.api}
					settings={tenantContext.settings}
					userData={userContext.userData}
				/>
			)
			break;
		case 'slides':
			main = (
				<Slides />
			)
			break;
		case 'wordcontents':
			main = (
				<WordContent />
			)
			break;
		case 'outlookcontent':
			main = (
				<OutlookContent />
			)
			break;
		case 'sections':
			main = (
				<Sections />
			)
			break;
		case 'emailsignatures':
			main = (
				<EmailSignatures />
			)
			break;
		case 'customdata':
			main = (
				<CustomData
					accessToken={accessToken!}
					domain={tenantContext.tenantName}
					api={tenantContext.api}
					groupIds={tenantContext.groupIds}
					settings={tenantContext.settings}
				/>
			)
			break;
		case 'userdata':
			main = (
				<UserData
					updateSettings={() => UpdateSettings(tenantContext.api, tenantContext.tenantName, userContext.userData)}
					settings={tenantContext.settings}
					accessToken={accessToken!}
					domain={tenantContext.tenantName}
					api={tenantContext.api}
				/>
			)
			break;
		case 'accounts':
			main = (
				<Accounts
					setMessage={(message) => { UpdateMessage(message) }}
					userData={userContext.userData}
					setUserData={(userData) => { setUserContext({ ...userContext, userData: userData }) }}
					accessToken={accessToken!}
					domain={tenantContext.tenantName}
					api={tenantContext.api}
					isTestDomain={tenantContext.settings.enableTestDomain}
					settings={tenantContext.settings}
				/>
			)
			break;
		case 'excel2users':
			main = (
				<Excel2Users
					setMessage={(message) => { UpdateMessage(message) }}
					userData={userContext.userData}
					accessToken={accessToken!}
					domain={tenantContext.tenantName}
					api={tenantContext.api}
					settings={tenantContext.settings}
					isTestDomain={tenantContext.settings.enableTestDomain}
				/>
			)
			break;
		case 'groups':
			main = (
				<Groups />
			)
			break;
		case 'userdatagroups':
			main = (
				<UserDataGroups />
			)
			break;
		case 'statistics':
			main = (
				<Statistics
					extendedStatistics={userContext.userData.isRehngruppen}
				/>
			)
			break;
		case 'domains':
			main = (
				<Domains
					updateUserData={() => { LoginSession(() => { }, () => { }, accessToken!) }}
					userData={userContext.userData}
				/>
			)
			break;
		case 'adminaccounts':
			main = (
				<AdminAccounts
					accessToken={accessToken!}
					userData={userContext.userData}
				/>
			)
			break;
		case 'customers':
			main = (
				<Customers />
			)
			break;
		case 'academy':
			main = (
				<Academy />
			)
			break;
		case 'tutorials':
			main = (
				<Tutorials />
			)
			break;
		case 'excel2web':
			main = (
				<Excel2Web />
			)
			break;
		case 'ai':
			main = (
				<AI />
			)
			break;
		case 'esef':
			main = (
				<Esef />
			)
			break;
		default:
			main = (<></>)
			break;
	}

	return (
		<Stack.Item className="ms-Grid-col ms-bgColor-gray10" styles={{ root: { height: '100%' } }}>
			<React.Suspense fallback={<LazyFallback />}>
				{main}
			</React.Suspense>
		</Stack.Item>
	);
}

export default MainPage

